import React, { useCallback, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { Alert, Card, Form, notification, Tabs } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import JoditEditor from 'jodit-react';
import CustomButton from '../../components/custom-button';
import PageTitle from '../../components/page-title';
import { useCurrentRoles } from '../../hooks/use-current-roles';
import { editorConfig } from '../news-manager/component/ModalCreateEdit';
import AxiosUtils from '../../utils/axios-utils';
import { API } from '../../service/api-route';

const initialContent = {
  content: '',
  textAppInfo: '',
  textLegalPolicy: '',
  textHp: '',
  linkContact: '',
};

function PolicyManager() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialContent);
  const [update, setUpdate] = useState(false);

  // PUT /rules chỉ ADMIN. Chỉ khoá nút khi đã biết chắc là không phải ADMIN.
  const { loaded: rolesLoaded, roles, isAdmin } = useCurrentRoles();
  const readOnly = rolesLoaded && roles.length > 0 && !isAdmin;

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const res: any = await AxiosUtils.put(`${API.putRules}`, {
        ...data,
      });
      if (res?.status === 200) {
        setUpdate(!update);
        notification.success({
          message: 'Thành công',
          description: 'Sửa thành công!',
          duration: 4,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRules = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.rules);
        setData((prevState) => {
          return {
            ...prevState,
            textHp: res.data.textHp,
            content: res.data.content,
            textLegalPolicy: res.data.textLegalPolicy,
            textAppInfo: res.data.textAppInfo,
            linkContact: res.data.linkContact,
          };
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, [update]);

  const onBlurContent = useCallback(
    (newContent) => {
      setData((prevState) => {
        return {
          ...prevState,
          content: newContent,
        };
      });
    },
    [setData]
  );

  const onBlurTextHp = useCallback(
    (newContent) => {
      setData((prevState) => {
        return {
          ...prevState,
          textHp: newContent,
        };
      });
    },
    [setData]
  );

  const onBlurTextLegalPolicy = useCallback(
    (newContent) => {
      setData((prevState) => {
        return {
          ...prevState,
          textLegalPolicy: newContent,
        };
      });
    },
    [setData]
  );

  const onBlurTextAppInfo = useCallback(
    (newContent) => {
      setData((prevState) => {
        return {
          ...prevState,
          textAppInfo: newContent,
        };
      });
    },
    [setData]
  );

  const onBlurLinkContact = useCallback(
    (newContent) => {
      setData((prevState) => {
        return {
          ...prevState,
          linkContact: newContent,
        };
      });
    },
    [setData]
  );

  // Nội dung chỉ được ghi vào state khi rời ô soạn thảo (onBlur); onChange để trống.
  const onChange = useCallback(() => undefined, []);

  // Mỗi tab một ô soạn thảo. Tab chỉ dựng khi mở lần đầu và giữ lại sau đó
  // (không huỷ), nên nội dung đang sửa ở tab khác không mất; nút Lưu gửi cả 5 mục.
  const renderEditor = (hint: string, value: string, onBlur: (newContent: string) => void) => (
    <div className={styles.pane}>
      <p className={styles.hint}>{hint}</p>
      <div style={{ maxWidth: editorConfig.width, margin: '0 auto' }}>
        <JoditEditor
          value={value}
          config={editorConfig as any}
          onBlur={onBlur}
          onChange={onChange}
        />
      </div>
    </div>
  );

  return (
    <div>
      <PageTitle
        title="Chính sách HP"
        extra={
          <CustomButton
            bgColor="secondary-green"
            text="Lưu thay đổi"
            icon={<SaveOutlined />}
            loading={loading}
            disabled={readOnly}
            onClick={() => form.submit()}
          />
        }
      />
      {readOnly && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Bạn chỉ có quyền xem trang này — chỉ ADMIN lưu được nội dung."
        />
      )}
      <Form name="form" form={form} onFinish={onFinish} autoComplete="off">
        <Card styles={{ body: { padding: 0 } }}>
          <Tabs
            className={styles.tabs}
            items={[
              {
                key: 'content',
                label: 'Quy tắc cộng đồng',
                children: renderEditor(
                  'Hiện ở màn “Quy tắc cộng đồng” trên app (mở được cả từ màn đăng ký).',
                  data.content,
                  onBlurContent
                ),
              },
              {
                key: 'textHp',
                label: 'Về HP Media App',
                children: renderEditor(
                  'Hiện ở màn “Thông tin về chúng tôi” trên app.',
                  data.textHp,
                  onBlurTextHp
                ),
              },
              {
                key: 'textLegalPolicy',
                label: 'Pháp lý chính sách',
                children: renderEditor(
                  'Hiện ở màn “Pháp lý và chính sách” trên app.',
                  data.textLegalPolicy,
                  onBlurTextLegalPolicy
                ),
              },
              {
                key: 'textAppInfo',
                label: 'Thông tin ứng dụng',
                children: renderEditor(
                  'Hiện ở màn “Thông tin về ứng dụng” trên app.',
                  data.textAppInfo,
                  onBlurTextAppInfo
                ),
              },
              {
                key: 'linkContact',
                label: 'Liên kết liên hệ',
                children: renderEditor(
                  'Nút “Liên hệ với chúng tôi” trên app mở thẳng nội dung ô này như một đường link — chỉ dán đúng một địa chỉ https://…, không định dạng chữ.',
                  data.linkContact,
                  onBlurLinkContact
                ),
              },
            ]}
          />
        </Card>
        <p className={styles.hint} style={{ marginTop: 12 }}>
          Nút “Lưu thay đổi” lưu cả 5 mục cùng lúc. Nội dung được ghi nhận khi bạn bấm ra ngoài ô
          soạn thảo.
        </p>
      </Form>
    </div>
  );
}

export default PolicyManager;
